import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ApiError, unwrap } from '@/api/envelope';
import { acceptInvitation, lookUpInvitation } from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { PasswordField } from '@/components/ui/PasswordField';

import { AuthLayout } from './AuthLayout';
import { acceptInvitationSchema, type AcceptInvitationValues } from './schemas';

/** Words for the role, which arrives SHOUTED because it is a stored value. */
const ROLE_WORDS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
};

/**
 * What the link turned out to be. `gone` and `invalid` are separate because
 * the person can do something different about each: an expired link needs a
 * new one, and a used one means they already have an account.
 */
type LinkState = 'checking' | 'ok' | 'expired' | 'used' | 'invalid' | 'unreachable';

function stateFromError(error: unknown): LinkState {
  if (!(error instanceof ApiError)) return 'unreachable';
  // The backend answers 410 with exactly "expired" or "used" so this can
  // branch without reading prose. 404 covers revoked and never-existed, which
  // deliberately share one answer.
  if (error.status === 410) {
    return error.serverMessage === 'used' ? 'used' : 'expired';
  }
  if (error.status === 404) return 'invalid';
  return 'unreachable';
}

/**
 * The page an invitation link opens. No sign-in: the person being invited has
 * no account to sign in with, and the link is what proves they were invited.
 *
 * It looks the link up before asking for anything, so nobody types a password
 * into a form for an invitation that expired last week. Whether a password is
 * wanted at all comes from the lookup: somebody who already has a portal
 * account and is being added to a second organization keeps the password they
 * have.
 */
export function AcceptInvitationPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();

  const [formError, setFormError] = useState<string | null>(null);
  const [acceptFailed, setAcceptFailed] = useState<LinkState | null>(null);
  const headingRef = useRef<HTMLDivElement>(null);

  const invitation = useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => {
      const reply = await lookUpInvitation({ path: { token }, throwOnError: true });
      return unwrap(reply.data);
    },
    // A dead link does not become live by asking again, and each retry is
    // another second of "Checking your invitation…".
    retry: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInvitationValues>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const accept = useMutation({
    mutationFn: async (values: AcceptInvitationValues) => {
      await acceptInvitation({
        path: { token },
        // Omitted entirely when the account already has one. Sending it would
        // be refused, because silently discarding a typed password is how
        // people come to believe they changed it.
        body: needsPassword ? { password: values.password } : {},
        throwOnError: true,
      });
    },
    onMutate: () => setFormError(null),
    onSuccess: () => {
      void navigate(ROUTES.signIn, {
        replace: true,
        state: {
          message: 'Your account is ready. Sign in with the address below.',
          email: invitation.data?.email,
        },
      });
    },
    onError: (error) => {
      const state = stateFromError(error);
      // A link that died while the form was open replaces the form. The typed
      // password lived only in form state and goes with it.
      if (state === 'expired' || state === 'used' || state === 'invalid') {
        setAcceptFailed(state);
        return;
      }
      setFormError(
        (error instanceof ApiError ? error.serverMessage : null) ??
          'We could not set your password. Please try again.',
      );
    },
  });

  const state: LinkState =
    acceptFailed ??
    (invitation.isPending
      ? 'checking'
      : invitation.isError
        ? stateFromError(invitation.error)
        : 'ok');

  const needsPassword = invitation.data?.needs_password !== false;

  // Move focus to the heading when a state replaces the form, so somebody
  // using a screen reader hears what happened instead of being left on a
  // control that no longer exists.
  useEffect(() => {
    if (state === 'checking' || state === 'ok') return;
    headingRef.current?.focus();
  }, [state]);

  if (state === 'checking') {
    return (
      <AuthLayout title="Checking your invitation…">
        <Typography variant="body2" color="text.secondary">
          One moment.
        </Typography>
      </AuthLayout>
    );
  }

  if (state !== 'ok') {
    const { title, body, showSignIn } = describeDeadLink(state);
    return (
      <AuthLayout title={title}>
        <Stack spacing={2}>
          <Alert severity="info" role="alert" ref={headingRef} tabIndex={-1}>
            {body}
          </Alert>
          {showSignIn ? (
            <Link component={RouterLink} to={ROUTES.signIn} variant="body2">
              Go to sign in
            </Link>
          ) : null}
          {state === 'unreachable' ? (
            <Button variant="secondary" onClick={() => void invitation.refetch()}>
              Try again
            </Button>
          ) : null}
        </Stack>
      </AuthLayout>
    );
  }

  const details = invitation.data!;

  return (
    <AuthLayout
      title={`Join ${details.organization_name}`}
      description={
        `You have been invited as ${ROLE_WORDS[details.role] ?? details.role}, ` +
        `at ${details.email}.`
      }
      error={formError}
    >
      {needsPassword ? (
        <Stack
          component="form"
          spacing={2}
          noValidate
          onSubmit={(event) => {
            void handleSubmit((values) => accept.mutate(values))(event);
          }}
        >
          <Field
            label="Choose a password"
            error={errors.password?.message}
            hint="At least 8 characters, with an upper case letter, a lower case letter and a digit."
          >
            <PasswordField {...register('password')} fullWidth />
          </Field>

          <Field label="Type it again" error={errors.confirmPassword?.message}>
            <PasswordField {...register('confirmPassword')} fullWidth />
          </Field>

          <Button type="submit" variant="primary" loading={accept.isPending}>
            Set password and continue
          </Button>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            You already have an ActivistARC portal account, so there is no new password to
            choose. Accepting adds this organization to it.
          </Typography>
          <Button
            variant="primary"
            loading={accept.isPending}
            onClick={() => accept.mutate({ password: '', confirmPassword: '' })}
          >
            Join {details.organization_name}
          </Button>
        </Stack>
      )}
    </AuthLayout>
  );
}

function describeDeadLink(state: LinkState) {
  switch (state) {
    case 'expired':
      return {
        title: 'This invitation has expired',
        body: 'Ask whoever invited you to send a new one.',
        showSignIn: false,
      };
    case 'used':
      return {
        title: 'This invitation has already been used',
        body: 'Your account is already set up. Sign in with the address it was sent to.',
        showSignIn: true,
      };
    case 'unreachable':
      return {
        title: 'We could not check your invitation',
        body: 'Something went wrong reaching the server. Try again in a moment.',
        showSignIn: false,
      };
    default:
      return {
        // Revoked and never-existed share this, so a guessed token learns
        // nothing about whether an address was ever invited.
        title: 'This invitation is not valid',
        body: 'Ask whoever invited you to send a new one.',
        showSignIn: false,
      };
  }
}
